import Queue from "nq";
import { formatDate, formatTime } from "../../components/common/Converter";
import objectToOption from "../../components/InputPointer/objectToOption";
import { HIERARCHY_COLLECTIONS } from "../../utils/hierarchy";
import { findObjectUseCase } from "../object";
import { getAllSchemasUseCase } from "../schema/usecases";
import { getCurrentUserUseCase } from "../user";
import { EXCLUDED_AUDIT_COLLECTIONS } from "./auditLogConstants";

const SKIP_KEYS = new Set([
  "id",
  "acl",
  "createdAt",
  "updatedAt",
  "updated",
  "created",
  "password",
  "confirmPassword",
  "oldPassword",
  "newPassword",
]);

const COMMON_LABEL_FIELDS = [
  "name",
  "title",
  "username",
  "email",
  "code",
  "agency",
  "description",
  "label",
  "reference_no",
  "referenceNo",
  "position_title",
  "jobTitle",
  "Firstname",
  "surname",
];

function isOpaqueId(value) {
  if (value == null || value === "") return false;
  const s = String(value).trim();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) {
    return true;
  }
  return s.length >= 24 && !/\s/.test(s);
}

function isReadableLabel(value) {
  return value != null && String(value).trim() !== "" && !isOpaqueId(value);
}

function camelToLabel(str) {
  if (!str) return str;
  return String(str)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase());
}

function getFieldLabel(schema, key) {
  const label = schema?.fields?.[key]?.label;
  return label != null && label !== "" ? label : camelToLabel(key);
}

function getIndexFields(fields) {
  if (!fields) return ["name"];

  const indexed = Object.entries(fields)
    .filter(([, def]) => def?._index || def?.index)
    .sort(([, a], [, b]) => (a.index ?? a._index ?? 0) - (b.index ?? b._index ?? 0))
    .map(([key]) => key);

  if (indexed.length) return indexed;

  const common = COMMON_LABEL_FIELDS.filter((key) => fields[key]);
  return common.length ? common : ["name"];
}

function getReadableFromObject(val) {
  if (!val || typeof val !== "object" || Array.isArray(val)) return null;

  if (val.Firstname || val.surname) {
    const name = [val.Firstname, val.Middlename, val.surname, val.Nameextension]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (name) return name;
  }

  const parts = [
    val.name,
    val.title,
    val.username,
    val.email,
    val.code,
    val.agency,
    val.label,
    val.description,
    val.position_title,
    val.jobTitle,
  ].filter(Boolean);

  if (parts.length) return parts.join(" ").trim();
  return null;
}

function scanRecordForLabel(record, schema) {
  if (!record || typeof record !== "object") return null;

  const readable = getReadableFromObject(record);
  if (isReadableLabel(readable)) return readable;

  const fields = schema?.fields || {};
  const indexes = getIndexFields(fields);
  for (const key of indexes) {
    const value = record[key];
    if (isReadableLabel(value)) return String(value).trim();
  }

  for (const key of COMMON_LABEL_FIELDS) {
    const value = record[key];
    if (isReadableLabel(value)) return String(value).trim();
  }

  for (const [key, def] of Object.entries(fields)) {
    if (SKIP_KEYS.has(key)) continue;
    if (def?.type !== "String" && def?.type !== "Number") continue;
    const value = record[key];
    if (isReadableLabel(value)) return String(value).trim();
  }

  return null;
}

function getRecordLabel(record, schema) {
  if (!record || typeof record !== "object") return null;

  const scanned = scanRecordForLabel(record, schema);
  if (scanned) return scanned;

  const indexes = getIndexFields(schema?.fields);
  const optionLabel = objectToOption(record, indexes).label;
  if (isReadableLabel(optionLabel)) return String(optionLabel).trim();

  return null;
}

async function resolveRecordLabel(record, schema, schemas, cache) {
  const syncLabel = getRecordLabel(record, schema);
  if (syncLabel) return syncLabel;

  const collection = schema?.collection;
  const id = record?.id;
  if (!collection || !id) return null;

  const cacheKey = `record:${collection}:${id}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const indexes = getIndexFields(schema?.fields);
  const keys = [...new Set([...indexes, ...COMMON_LABEL_FIELDS])];

  try {
    const results = await findObjectUseCase().execute(collection, {
      where: { id },
      limit: 1,
      ...(keys.length ? { keys } : {}),
    });
    const full = results?.[0];
    const label = full ? scanRecordForLabel(full, schema) : null;
    cache.set(cacheKey, label);
    return label;
  } catch (e) {
    console.error(e.message || e);
    cache.set(cacheKey, null);
    return null;
  }
}

function formatValue(val) {
  if (val == null) return "—";
  if (isOpaqueId(val)) return "—";

  if (typeof val === "object") {
    if (Array.isArray(val)) {
      return val.length ? `[${val.length} item(s)]` : "[]";
    }

    const readable = getReadableFromObject(val);
    if (isReadableLabel(readable)) return readable;

    if (val.id && isOpaqueId(val.id) && Object.keys(val).length <= 2) {
      return "—";
    }

    return JSON.stringify(val).slice(0, 80);
  }

  const s = String(val);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
}

function looksLikePointerRef(val) {
  if (val == null || val === "") return false;
  if (typeof val === "object" && !Array.isArray(val) && val.id) return true;
  return isOpaqueId(val);
}

function getPointerId(val) {
  if (val == null || val === "") return null;
  if (typeof val === "object" && !Array.isArray(val)) return val.id || null;
  return val;
}

async function resolvePointerValue(val, fieldDef, schemas, cache) {
  if (val == null || val === "") return "—";
  if (Array.isArray(val)) return formatValue(val);

  if (typeof val === "object") {
    const readable = getReadableFromObject(val);
    if (isReadableLabel(readable)) return readable;
  }

  const id = getPointerId(val);
  if (!id) return formatValue(val);

  const target = fieldDef?.target || fieldDef?.targetClass;
  if (!target) {
    return isOpaqueId(id) ? "—" : formatValue(id);
  }

  const cacheKey = `${target}:${id}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const targetSchema = schemas.find((s) => s.collection === target);
  const indexes = getIndexFields(targetSchema?.fields);
  let label = null;

  try {
    const keys = [...new Set([...indexes, ...COMMON_LABEL_FIELDS])];
    const results = await findObjectUseCase().execute(target, {
      where: { id },
      limit: 1,
      ...(keys.length ? { keys } : {}),
    });
    const resolved = results?.[0];
    if (resolved) {
      const option = objectToOption(resolved, indexes);
      label =
        scanRecordForLabel(resolved, targetSchema) ||
        (isReadableLabel(option.label) ? option.label : null);
    }
  } catch (e) {
    console.error(e.message || e);
  }

  label = label || "—";
  cache.set(cacheKey, label);
  return label;
}

async function formatRelationValue(val, fieldDef, schemas, cache) {
  if (!Array.isArray(val)) return formatValue(val);
  if (!val.length) return "[]";
  const labels = await Promise.all(
    val.map((item) =>
      resolvePointerValue(
        item,
        { target: fieldDef?.target || fieldDef?.targetClass },
        schemas,
        cache,
      ),
    ),
  );
  return labels.join(", ");
}

async function formatFieldValue(val, fieldDef, schemas, cache, fieldKey) {
  const target =
    fieldDef?.target ||
    fieldDef?.targetClass ||
    (looksLikePointerRef(val) ? HIERARCHY_COLLECTIONS[fieldKey] : undefined);

  if (fieldDef?.type === "Relation") {
    return formatRelationValue(
      val,
      { ...fieldDef, target: target || fieldDef?.target || fieldDef?.targetClass },
      schemas,
      cache,
    );
  }
  if (fieldDef?.type === "Pointer" || target) {
    return resolvePointerValue(val, { ...fieldDef, target }, schemas, cache);
  }
  return formatValue(val);
}

function isUnresolvedDisplay(value) {
  if (value == null) return true;
  const s = String(value).trim();
  return s === "" || s === "—" || s === "-";
}

function valuesAreEffectivelyEqual(prev, next) {
  if (JSON.stringify(prev) === JSON.stringify(next)) return true;

  const prevEmpty = prev == null || prev === "";
  const nextEmpty = next == null || next === "";
  if (prevEmpty && nextEmpty) return true;

  const prevId = getPointerId(prev);
  const nextId = getPointerId(next);
  if (prevId && nextId) {
    return String(prevId) === String(nextId);
  }

  if (prevEmpty || nextEmpty) return false;

  if (Array.isArray(prev) && Array.isArray(next)) {
    const toKey = (item) =>
      item && typeof item === "object" ? item.id ?? JSON.stringify(item) : item;
    const prevKeys = prev.map(toKey).sort().join("|");
    const nextKeys = next.map(toKey).sort().join("|");
    return prevKeys === nextKeys;
  }

  return String(prev).trim() === String(next).trim();
}

function shouldIncludeFieldChange(fromStr, toStr, prev, next) {
  if (valuesAreEffectivelyEqual(prev, next)) return false;
  if (fromStr === toStr) return false;
  if (isUnresolvedDisplay(fromStr) && isUnresolvedDisplay(toStr)) return false;
  return true;
}

function roleIdentity(role) {
  if (role == null || role === "") return "";
  if (typeof role !== "object") return String(role);
  if (role.id != null && role.id !== "") return String(role.id);
  if (role.name != null && role.name !== "") return String(role.name);
  return "";
}

function roleIdentityKey(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return "";
  return roles.map(roleIdentity).filter(Boolean).sort().join("|");
}

async function fetchRoleName(id, cache) {
  const cacheKey = `user-role-name:${id}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  let label = null;
  try {
    const results = await findObjectUseCase().execute("roles", {
      where: { id },
      limit: 1,
      keys: ["name"],
    });
    const name = results?.[0]?.name;
    if (isReadableLabel(name)) label = String(name).trim();
  } catch (e) {
    console.error(e.message || e);
  }

  cache.set(cacheKey, label);
  return label;
}

async function resolveUserRoleLabel(role, cache) {
  if (role == null || role === "") return null;

  if (typeof role !== "object") {
    const fetched = await fetchRoleName(role, cache);
    if (fetched) return fetched;
    return isReadableLabel(role) ? String(role).trim() : null;
  }

  const id = role.id;
  const name = role.name;
  const nameIsCopyOfId =
    name != null && id != null && String(name) === String(id);

  if (isReadableLabel(name) && !nameIsCopyOfId) {
    return String(name).trim();
  }

  if (id != null && id !== "") {
    const fetched = await fetchRoleName(id, cache);
    if (fetched) return fetched;
  }

  if (isReadableLabel(name)) return String(name).trim();
  if (isReadableLabel(id)) return String(id).trim();
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

async function formatUserRoles(roles, cache) {
  if (!Array.isArray(roles) || roles.length === 0) return "none";
  const labels = [];
  for (const role of roles) {
    const label = await resolveUserRoleLabel(role, cache);
    if (label) labels.push(label);
  }
  return labels.length ? labels.join(", ") : "none";
}

async function formatRecordTarget(record, schema, schemas, cache) {
  const label = await resolveRecordLabel(record, schema, schemas, cache);
  if (label) return `"${label}"`;
  return "";
}

async function buildChangeMessage(
  action,
  formattedCollection,
  object,
  previousData,
  activity,
  schema,
  schemas,
) {
  const label = (key) => getFieldLabel(schema, key);
  const fields = schema?.fields || {};
  const cache = new Map();

  if (action === "UPDATE" && previousData && typeof previousData === "object") {
    const changes = [];
    const recordTarget = await formatRecordTarget(
      previousData,
      schema,
      schemas,
      cache,
    );
    // Only consider fields present in the update payload (object) — omit undefined so we
    // don't report "changed to —" for fields the client didn't send.
    const payloadKeys = Object.keys(object || {});
    for (const key of payloadKeys) {
      if (SKIP_KEYS.has(key)) continue;
      const next = object[key];
      if (next === undefined) continue;
      const prev = previousData[key];
      // User role saves store `{ id, name: id }`. The generic formatter then
      // renders both sides as "—" and drops the change. Name the roles here
      // only; every other field keeps the shared comparison below.
      if (formattedCollection === "USERS" && key === "roles") {
        if (roleIdentityKey(prev) === roleIdentityKey(next)) continue;
        const fromRoles = await formatUserRoles(prev, cache);
        const toRoles = await formatUserRoles(next, cache);
        if (fromRoles === toRoles) continue;
        changes.push(`${label(key)}: from "${fromRoles}" → to "${toRoles}"`);
        continue;
      }
      if (valuesAreEffectivelyEqual(prev, next)) continue;
      const fromStr = await formatFieldValue(
        prev,
        fields[key],
        schemas,
        cache,
        key,
      );
      const toStr = await formatFieldValue(
        next,
        fields[key],
        schemas,
        cache,
        key,
      );
      if (!shouldIncludeFieldChange(fromStr, toStr, prev, next)) continue;
      changes.push(`${label(key)}: from "${fromStr}" → to "${toStr}"`);
    }
    const target = recordTarget ? ` ${recordTarget}` : "";
    if (changes.length) {
      return `Updated ${formattedCollection}${target}: ${changes.join("; ")}`;
    }
    return `Updated ${formattedCollection}${target}`;
  }
  if (action === "ADD") {
    const parts = [];
    const recordTarget = await formatRecordTarget(object, schema, schemas, cache);
    for (const key of Object.keys(object || {})) {
      if (SKIP_KEYS.has(key)) continue;
      const valueStr = await formatFieldValue(
        object[key],
        fields[key],
        schemas,
        cache,
        key,
      );
      if (isUnresolvedDisplay(valueStr)) continue;
      parts.push(`${label(key)}=${valueStr}`);
    }
    const summary = parts.length ? parts.slice(0, 5).join(", ") : "record";
    const target = recordTarget ? ` ${recordTarget}` : "";
    return `Added ${formattedCollection}${target}: ${summary}`;
  }
  if (action === "DELETE") {
    const parts = [];
    const recordTarget = await formatRecordTarget(object, schema, schemas, cache);
    for (const key of Object.keys(object || {})) {
      if (SKIP_KEYS.has(key)) continue;
      const valueStr = await formatFieldValue(
        object[key],
        fields[key],
        schemas,
        cache,
        key,
      );
      if (isUnresolvedDisplay(valueStr)) continue;
      parts.push(`${label(key)}=${valueStr}`);
    }
    const summary = parts.length
      ? parts.slice(0, 5).join(", ")
      : (await resolveRecordLabel(object, schema, schemas, cache)) || "record";
    const target = recordTarget ? ` ${recordTarget}` : "";
    return `Deleted ${formattedCollection}${target}: ${summary}`;
  }
  if (formattedCollection === "IN" || formattedCollection === "OUT") {
    return activity || `You have successfully ${action} ${formattedCollection}`;
  }
  return `You have successfully ${action} ${formattedCollection} last ${new Date()}`;
}

class AuditLogsUseCase {
  async execute(collection, object, activity, action, options = {}) {
    if (EXCLUDED_AUDIT_COLLECTIONS.has(collection)) return;
    const dateNow = new Date();
    const logDate = formatDate(dateNow);
    const logTime = formatTime(dateNow);
    const formattedCollection =
      typeof collection === "string" && !["IN", "OUT"].includes(collection)
        ? collection.replace(/_/g, " ").toUpperCase()
        : collection;

    try {
      const currentUserData = await new getCurrentUserUseCase().execute();
      const { previousData, schema } = options;
      const schemas = await getAllSchemasUseCase().execute();

      const data = {
        logDate: `${logDate} at ${logTime}`,
        activity,
        user: currentUserData.username,
      };

      const message = await buildChangeMessage(
        action,
        formattedCollection,
        object,
        previousData,
        activity,
        schema,
        schemas,
      );

      const logPayload = { ...data, message };
      try {
        return await new Queue.Document().create("audit_logs", logPayload);
      } catch (e) {
        console.error(e.message || e);
      }
    } catch (e) {
      console.error(e.message || e);
    }
  }
}
// }

export default AuditLogsUseCase;
